import React, { useState, useRef, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import DatePicker from 'react-datepicker';
import { parse, format } from 'date-fns';
import ReactTooltip from 'react-tooltip';
import classNames from 'classnames';

import Spinner from '../../../../components/global/Spinner';
import { AVATAR_SIZE_72 } from '../../../../constants/avatar-sizes';
import Button from '../../../../components/global/Button';
import Alert from '../../../../components/global/Alert';
import IdentityAvatarContainer from '../../../../containers/global/identity/IdentityAvatarContainer';
import FormGroup from '../../../../components/global/FormGroup';
import useRosterIdentityUpdate from './useRosterIdentityUpdate';
import UserAddressFormModal from '../../../../components/global/user-address/UserAddressFormModal';
import UserAddressDisplayEditBox from '../../../../components/global/user-address/UserAddressDisplayEditBox';
import PhoneInput from '../../../../components/global/PhoneInput';
import { isValidPhone } from '../../../../util/phone';
import { fileInputAccept } from '../../../../constants/photo-upload';
import { getFormikTouchedErrors } from '../../../../util/formik';
import Label from '../../../../components/global/Label';
import CustomControl from '../../../../components/global/CustomControl';
import {
  getIdentityFullName,
  getRosterIdentityMainLinkedIdentity,
} from '../../../../util/identity';
import FeatureBlock from '../../../../components/contextual/FeatureBlock';
import useBasename from '../../../../components/routing/useBasename';
import { canAccessNoPostToSocialOnBasename } from '../../../../util/product-permissions';
import { photoUploadFilter } from '../../../../util/photo-upload';
import BoxedCustomControl from '../../../../components/global/BoxedCustomControl';
import AlbumSearchSelect from './components/AlbumSearchSelect';
import DownloadPhotoButton from '../../../../containers/global/DownloadPhotoButtonContainer';
import uuidv4 from '../../../../util/uuid';
import { getThumbBySize } from '../../../../util/photo';
import { IMAGE_SIZE_CODE } from '../../../../constants/image-size-codes';
import { ACCOUNT_TYPE } from '../../views/AlbumFolder/AlbumFolders/AlbumFolderMembers/components/AlbumFolderIdentityMembershipDetails/constants';

const JOIN_TYPE = {
  ALBUM_FOLDER: 'ALBUM_FOLDER',
  ALBUMS: 'ALBUMS',
};

const propTypes = {
  account: PropTypes.shape({
    id: PropTypes.string,
  }),
  rosterIdentity: PropTypes.shape({
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    selfie_photo: PropTypes.shape({
      id: PropTypes.string,
    }),
  }),
  identityId: PropTypes.string,
  loading: PropTypes.bool,
  photoUploadSuccess: PropTypes.func.isRequired,
  onRosterIdentityCreated: PropTypes.func,
  onRosterIdentityUpdated: PropTypes.func,
  defaultEditAddress: PropTypes.bool,
  onRequestClose: PropTypes.func.isRequired,
  albumId: PropTypes.string,
  albumFolderId: PropTypes.string,
  albumFolderSelectionEnabled: PropTypes.bool,
  albumSelectionEnabled: PropTypes.bool,
  isSubscription: PropTypes.bool,
  organizationRefIdEnabled: PropTypes.bool,
  autoFocus: PropTypes.string,
};

const RosterIdentityUpdate = ({
  account,
  rosterIdentity,
  identityId,
  loading,
  photoUploadSuccess,
  onRosterIdentityCreated,
  onRosterIdentityUpdated,
  defaultEditAddress = false,
  onRequestClose,
  albumId,
  albumFolderId,
  albumFolderSelectionEnabled,
  albumSelectionEnabled,
  isSubscription,
  organizationRefIdEnabled = false,
  autoFocus = 'first_name',
}) => {
  const basename = useBasename();

  // Used to get base phone number & address:
  const mainLinkedIdentity = getRosterIdentityMainLinkedIdentity(
    rosterIdentity,
  );
  const fileInput = useRef(null);
  const [fileUrl, setFileUrl] = useState('');
  const [editAddress, setEditAddress] = useState(defaultEditAddress);
  const [joinType, setJoinType] = useState(
    albumFolderSelectionEnabled ? JOIN_TYPE.ALBUM_FOLDER : JOIN_TYPE.ALBUMS,
  );
  const [selectedAlbums, setSelectedAlbums] = useState([]);

  const isCreate = !rosterIdentity?.id;
  const isMemberCreate = isCreate && (albumFolderId || albumId);

  const {
    updating,
    updateError,
    updateSuccess,
    onRosterIdentityUpdate,
  } = useRosterIdentityUpdate({
    onPhotoUploadSuccess: ({ photoId, file: uploadedFile }) =>
      photoUploadSuccess(
        {
          id: photoId,
          file: uploadedFile,
          identityId: rosterIdentity?.id,
        },
        true,
      ),
  });

  const onFileChoose = event => {
    event.preventDefault();
    fileInput.current.click();
  };

  const isFormDisabled = values => {
    if (isCreate && !isValidPhone(values.phone_number)) {
      return true;
    }

    if (
      isCreate &&
      albumFolderId &&
      joinType === JOIN_TYPE.ALBUMS &&
      selectedAlbums.length === 0
    ) {
      return true;
    }

    return !values.first_name || !values.last_name || updating;
  };

  const onFormSubmit = async values => {
    if (isFormDisabled(values)) {
      return;
    }

    const { address, phone_number: phoneNumber, ...inputValues } = values;

    const isMarketingAccount =
      mainLinkedIdentity?.account?.type === ACCOUNT_TYPE.MARKETING;

    const rosterIdentityId = rosterIdentity?.id;

    const latestRosterIdentity = await onRosterIdentityUpdate({
      ...inputValues,
      date_of_birth: inputValues.date_of_birth
        ? format(inputValues.date_of_birth, 'yyyy-MM-dd')
        : null,
      identity_id: identityId,
      account_id: account.id,
      roster_identity_id: rosterIdentityId,
      linked_identities: [
        {
          id: !isMarketingAccount && mainLinkedIdentity?.id,
          phone_number: phoneNumber,
          addresses: address?.address_line_1 ? [address] : [],
        },
      ],
      linked_album_ids: (() => {
        if (!(albumId || joinType === JOIN_TYPE.ALBUMS)) {
          return [];
        }

        return albumId ? [albumId] : selectedAlbums.map(({ id }) => id);
      })(),
      linked_album_folder_ids:
        albumFolderId && joinType === JOIN_TYPE.ALBUM_FOLDER
          ? [albumFolderId]
          : [],
    });

    if (!latestRosterIdentity) {
      // Possible error
      return;
    }

    if (isCreate || isMarketingAccount) {
      onRosterIdentityCreated?.(latestRosterIdentity);
      onRequestClose();
    } else {
      onRosterIdentityUpdated?.(latestRosterIdentity);

      // If editing only the address, close the modal immediately after save
      if (defaultEditAddress) {
        onRequestClose();
      }
    }
  };

  if (loading) {
    return (
      <div className="text-center">
        <Spinner />
      </div>
    );
  }

  const photo = fileUrl
    ? { thumbnail_urls: [{ width: 0, height: 0, url: fileUrl }] }
    : rosterIdentity?.selfie_photo ??
      rosterIdentity?.current_matching_photo ??
      mainLinkedIdentity?.identity?.current_photo;

  const photoThumbnail = getThumbBySize(
    photo?.thumbnail_urls ?? [],
    IMAGE_SIZE_CODE.MEDIUM,
  );

  const mainLinkedIdentityPhotoOutdated =
    mainLinkedIdentity?.identity?.has_outdated_selfie;

  const validate = values => {
    const errors = {};

    if (!isValidPhone(values.phone_number)) {
      errors.phone_number =
        'Provided phone number is not valid. Please provide a valid one.';
    }

    return errors;
  };

  const photoContainerClasses = classNames({
    'text-center': true,
    'mb-3': !photo?.id,
  });

  return (
    <Formik
      validate={validate}
      initialValues={{
        selfie: null,
        first_name: rosterIdentity?.first_name ?? '',
        last_name: rosterIdentity?.last_name ?? '',
        phone_number: mainLinkedIdentity?.phone_number ?? '',
        address: mainLinkedIdentity?.addresses?.records[0] ?? {
          // Pass the name of the identity
          // as the default on an empty address
          // so that it's prepopulated.
          name: getIdentityFullName(rosterIdentity),
        },
        date_of_birth: rosterIdentity?.date_of_birth
          ? parse(rosterIdentity.date_of_birth, 'yyyy-MM-dd', new Date())
          : null,
        photo_share_blocked: rosterIdentity?.photo_share_blocked,
        organization_ref_id: rosterIdentity?.organization_ref_id,
      }}
      onSubmit={onFormSubmit}
      render={({
        values,
        errors,
        handleChange,
        handleBlur,
        handleSubmit,
        touched,
      }) => {
        const formikErrors = Object.values(
          getFormikTouchedErrors(errors, touched),
        );

        return (
          <Fragment>
            <form method="POST" onSubmit={handleSubmit}>
              {(updateError || formikErrors.length > 0) && (
                <Alert type="danger">
                  {updateError ||
                    formikErrors.map((message, index) => (
                      <p key={index}>{message}</p>
                    ))}
                </Alert>
              )}

              {updateSuccess && (
                <Alert type="success" dismissible>
                  {isCreate ? 'Created' : 'Updated'} successfully!
                </Alert>
              )}

              <div className={photoContainerClasses}>
                {photoThumbnail && (
                  <p className="my-1 text-xs text-normal text-light">
                    Hover to enlarge reference photo
                  </p>
                )}
                <a onClick={onFileChoose} href="#">
                  <IdentityAvatarContainer
                    size={AVATAR_SIZE_72}
                    identity={{
                      // A random ID will avoid a photo to be stuck there for new
                      // identities. Otherwise, it might look for the "undefined"
                      // ID and find a photo in the store.
                      id: uuidv4(),
                      ...(rosterIdentity || {}),
                      name: rosterIdentity?.first_name,
                      current_matching_photo: photo,
                    }}
                    selfieType="matching"
                    data-tip=""
                    data-for={`tooltip-identity-selfie-${rosterIdentity?.id}`}
                    data-place="top"
                  >
                    {photoThumbnail && (
                      <ReactTooltip
                        delayHide={250}
                        className="member-photos-tooltip"
                        id={`tooltip-identity-selfie-${rosterIdentity?.id}`}
                        backgroundColor="transparent"
                        arrowColor="transparent"
                        effect="solid"
                      >
                        <img src={photoThumbnail.url} alt="Identity Selfie" />
                      </ReactTooltip>
                    )}
                  </IdentityAvatarContainer>
                  <span className="d-block mt-1 text-sm">
                    {photo ? 'Edit' : 'Add'} reference photo
                  </span>
                </a>
                {mainLinkedIdentityPhotoOutdated && photo && (
                  <p className="text-danger text-xs">
                    The selfie shown for this identity is outdated and <br />
                    won&rsquo;t be used for matching. Please update it to
                    restore matching.
                  </p>
                )}
              </div>
              {photo?.id && (
                <div className="text-center mb-3">
                  <DownloadPhotoButton
                    downloadOriginal
                    photoId={photo?.id}
                    photoSize={IMAGE_SIZE_CODE.LARGE2X}
                    btnStyle="link"
                    btnSize="sm"
                  >
                    Download current reference photo
                  </DownloadPhotoButton>
                </div>
              )}

              <input
                id="upload-photo"
                type="file"
                accept={fileInputAccept}
                onChange={event => {
                  const [eventFile] = event.target.files;

                  if (!photoUploadFilter(eventFile)) {
                    return;
                  }

                  setFileUrl(URL.createObjectURL(eventFile));

                  handleChange({
                    target: {
                      name: 'selfie',
                      value: eventFile,
                    },
                  });
                }}
                ref={fileInput}
                hidden
                data-testid="roster-identity-update-upload-photo"
              />

              <div className="row">
                <div className="col-6">
                  <FormGroup
                    id="first_name"
                    name="first_name"
                    label="First Name"
                    value={values.first_name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    autoFocus={autoFocus === 'first_name'}
                    autoComplete="off"
                  />
                </div>

                <div className="col-6">
                  <FormGroup
                    id="last_name"
                    name="last_name"
                    label="Last Name"
                    value={values.last_name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    autoFocus={autoFocus === 'last_name'}
                    autoComplete="off"
                  />
                </div>
                {!isMemberCreate && (
                  <div className="col-6">
                    <div className="form-group">
                      <Label id="birthDate" labelNote="optional">
                        Birthday
                      </Label>

                      <DatePicker
                        id="birthDate"
                        name="date_of_birth"
                        className="form-control"
                        isClearable
                        maxDate={new Date()}
                        selected={values.date_of_birth}
                        onChange={date => {
                          date?.setHours(0, 0, 0, 0);

                          handleChange({
                            target: {
                              name: 'date_of_birth',
                              value: date,
                            },
                          });

                          handleBlur?.({
                            target: {
                              name: 'date_of_birth',
                            },
                          });
                        }}
                      />
                    </div>
                  </div>
                )}

                <div className="col-6">
                  <PhoneInput
                    id="phone_number"
                    label="Mobile Phone Number"
                    value={values.phone_number || '+1'}
                    onChange={phone => {
                      handleChange({
                        target: {
                          name: 'phone_number',
                          value: phone,
                        },
                      });
                    }}
                    required={isCreate || !!values.address}
                    disabled={
                      !!mainLinkedIdentity?.phone_number ||
                      (updateSuccess && values.phoneNumber)
                    }
                    onBlur={handleBlur}
                    autoFocus={autoFocus === 'phone_number'}
                    autoComplete="off"
                  />
                </div>
                {organizationRefIdEnabled && (
                  <div className="col-6">
                    <FormGroup
                      id="organization_ref_id"
                      name="organization_ref_id"
                      label="Ref ID"
                      value={values.organization_ref_id}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      autoFocus={autoFocus === 'organization_ref_id'}
                    />
                  </div>
                )}
                <FeatureBlock
                  condition={canAccessNoPostToSocialOnBasename(basename)}
                >
                  <div className="col-6">
                    <div className="form-group">
                      <Label id="noPostToSocial">No post to social</Label>
                      <CustomControl
                        type="checkbox"
                        id="npts"
                        size="sm"
                        checked={values.photo_share_blocked}
                        onChange={() =>
                          handleChange({
                            target: {
                              name: 'photo_share_blocked',
                              value: !values.photo_share_blocked,
                            },
                          })
                        }
                        label="Do not allow photos featuring this member to be posted to social via apps."
                      />
                    </div>
                  </div>
                </FeatureBlock>

                {(albumId || albumFolderId) && (
                  <div className="col-6">
                    <div className="form-group">
                      <Label id="join-identity-checkbox">Join Type</Label>
                      <p className="text-bold text-sm text-dark">
                        The entity created will be{' '}
                        {isSubscription
                          ? 'subscribed to'
                          : 'able to preview their faceblocked photos on'}{' '}
                        {(() => {
                          if (albumId) {
                            return 'the album';
                          }

                          return joinType === JOIN_TYPE.ALBUMS
                            ? 'the selected albums'
                            : 'the gallery';
                        })()}{' '}
                        {!isSubscription && 'prior to purchasing'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
              {albumFolderId && (
                <div className="row mb-2">
                  {albumFolderSelectionEnabled && (
                    <div className="col-12">
                      <BoxedCustomControl
                        id="album-folder-join"
                        type="checkbox"
                        label="Join the gallery"
                        description="Join all albums within this gallery."
                        onChange={() => {
                          setJoinType(JOIN_TYPE.ALBUM_FOLDER);
                        }}
                        checked={joinType === JOIN_TYPE.ALBUM_FOLDER}
                      />
                    </div>
                  )}
                  {albumFolderSelectionEnabled && albumSelectionEnabled && (
                    <div className="col-12">
                      <Label
                        id="label-or"
                        labelType="expanded"
                        className="w-100 text-center my-1"
                      >
                        OR
                      </Label>
                    </div>
                  )}
                  {albumSelectionEnabled && (
                    <div className="col-12">
                      <BoxedCustomControl
                        id="album-join"
                        type="checkbox"
                        label="Join albums"
                        description={`Specify the albums you want this person to be ${
                          isSubscription
                            ? 'subscribed to'
                            : 'able to preview their photos on'
                        }.`}
                        onChange={() => {
                          setJoinType(JOIN_TYPE.ALBUMS);
                        }}
                        checked={joinType === JOIN_TYPE.ALBUMS}
                      >
                        {joinType === JOIN_TYPE.ALBUMS && (
                          <AlbumSearchSelect
                            selectedAlbums={selectedAlbums}
                            albumFolderId={albumFolderId}
                            onSelectedAlbumsChange={newSelectedAlbums =>
                              setSelectedAlbums(newSelectedAlbums)
                            }
                          />
                        )}
                      </BoxedCustomControl>
                    </div>
                  )}
                </div>
              )}

              <UserAddressDisplayEditBox
                address={values.address}
                onClick={() => {
                  setEditAddress(true);
                }}
                className="form-group"
              />

              <div className="text-right">
                <Button
                  type="submit"
                  disabled={isFormDisabled(values)}
                  btnSize="sm"
                  className="m-0"
                >
                  {(() => {
                    if (updating) {
                      return 'Loading...';
                    }

                    return rosterIdentity ? 'Update' : 'Create';
                  })()}
                </Button>
              </div>
            </form>

            <UserAddressFormModal
              forceOpen={editAddress}
              address={values.address}
              onCancel={() => {
                setEditAddress(false);

                if (defaultEditAddress) {
                  onRequestClose();
                }
              }}
              onSuccess={address => {
                setEditAddress(false);
                handleChange({
                  target: {
                    name: 'address',
                    value: address,
                  },
                });

                if (!isCreate) {
                  // When updating, save right away:
                  handleSubmit();
                }
              }}
            />
          </Fragment>
        );
      }}
    />
  );
};

RosterIdentityUpdate.propTypes = propTypes;

export default RosterIdentityUpdate;
